import { useRef, useState, useEffect, useCallback } from "react";

import {
    Box,
    Card,
    CardActions,
    CardContent,
    IconButton,
    styled,
    Theme,
} from "@mui/material";
import FastRewind from "@mui/icons-material/FastRewind";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";

import { DateTime } from "luxon";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    selectInWindowMode,
    selectShowSidebar,
} from "features/core/store/selectors";

import { PlaybackTimeline, PlaybackTimelineRef } from "./PlaybackTimeline";

import { useAnimationFrameTicker } from "hooks/useAnimationFrameTicker";
import { CaseManagerConfiguration } from "types/AppConfig";
import CurrentTime from "./CurrentTime";
import { playbackSelectors } from "./store/selectors.playback";
import { caseViewerActions } from "./store/slice";
import { PlaybackSettingsPopOver } from "./PlaybackSettingsPopOver";
import { FastForward } from "@mui/icons-material";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { DossierExportButton } from "./DossierExportButton";

const IconButtonWhite = styled(IconButton)(({ theme }) => ({
    color: theme.palette.common.white,
    margin: "0px",
    ":disabled": {
        color: theme.palette.common.white,
        opacity: "30%",
    },
}));

export interface PlaybackControlProps {
    startDateTime: string;
    endDateTime: string;
    caseManagerConfiguration: CaseManagerConfiguration;
    drawerWidth: number;
    displayAsFloatingFooter: boolean;
}

export function PlaybackControls(props: PlaybackControlProps) {
    const dispatch = useAppDispatch();

    const userRequestedPlayMode = useAppSelector(
        playbackSelectors.selectUserRequestedPlayMode,
    );

    const inWindowMode = useAppSelector(selectInWindowMode);
    const showSidebar = useAppSelector(selectShowSidebar);
    const playbackPlaying = useAppSelector(
        playbackSelectors.selectPlaybackPlaying,
    );

    // ticker
    const onTick = useCallback(
        (elapsedPlayTimeInMs: number) => {
            dispatch(
                caseViewerActions.setElapsedPlayTimeInMs({
                    elapsedPlayTimeInMs,
                }),
            );
        },
        [dispatch],
    );

    const { startTicker, stopTicker, restartTicker, isTicking } =
        useAnimationFrameTicker(
            props.caseManagerConfiguration.animationFrameTickerTickEveryInMs,
            onTick,
        );

    useEffect(() => {
        const isNowTicking = isTicking();

        if (playbackPlaying && !isNowTicking) {
            startTicker();
        } else if (!playbackPlaying && isNowTicking) {
            if (userRequestedPlayMode === "play") {
                // save current playback position to enable playback from the current position when resumed after buffering
                dispatch(caseViewerActions.saveCurrentPlaybackPosition());
            }

            stopTicker();
        }
    }, [
        startTicker,
        stopTicker,
        playbackPlaying,
        isTicking,
        userRequestedPlayMode,
        dispatch,
    ]);

    // timeline
    const onAddToOrSubtractFromPlaybackDateTime = (
        addOrSubstract: "add" | "subtract",
        seconds: number,
    ) => {
        switch (addOrSubstract) {
            case "add":
                dispatch(
                    caseViewerActions.addSecondsToPlaybackStartDateTime({
                        seconds,
                    }),
                );
                break;
            case "subtract":
                dispatch(
                    caseViewerActions.subtractSecondsFromPlaybackStartDateTime({
                        seconds,
                    }),
                );
                break;
        }

        restartTicker();
    };

    const onSetPlaybackDateTime = (dateTime: DateTime) => {
        dispatch(
            caseViewerActions.setPlaybackStartDateTime({
                playbackStartDateTime: dateTime.toISO() as string,
            }),
        );

        restartTicker();
    };

    const userRequestedPlayModePlay =
        userRequestedPlayMode === "play" || userRequestedPlayMode === "resume";

    // when controls float we need to add bottom spacing to make sure user can see all persisted recordings properly
    const cardRef = useRef<HTMLDivElement | null>(null);
    const playbackTimeLineRef = useRef<PlaybackTimelineRef | null>(null);
    const [playbackControlsHeight, setPlaybackControlsHeight] = useState(0);
    const [showFullSize, setShowFullSize] = useState(true);

    const mouseLeaveTimeOut = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (cardRef.current) {
            cardRef.current?.addEventListener("transitionend", () => {
                if (playbackTimeLineRef.current) {
                    playbackTimeLineRef.current.redrawTimeline();
                }
            });
        }
    }, []);

    const cardLeftMargin = (theme: Theme) => {
        if (!inWindowMode) {
            return showSidebar ? `${props.drawerWidth * 2 * -1}px` : undefined; // two times draw width
        }

        return showSidebar
            ? `calc(${props.drawerWidth * -1}px - ${theme.spacing(6)} - .5vw)`
            : theme.spacing(-6); // sidebar width - main div padding - .5vw
    };

    const cardWidth = (theme: Theme) => {
        if (showFullSize) {
            return inWindowMode
                ? `calc(100% - ${showSidebar ? props.drawerWidth : 0}px - ${theme.spacing(6)})`
                : `calc(100% - ${theme.spacing(12)})`;
        }

        return "10%";
    };

    return (
        <>
            {props.displayAsFloatingFooter && (
                <Box
                    sx={(theme) => ({
                        height: `calc(${playbackControlsHeight}px + ${theme.spacing(5)})`,
                    })}
                />
            )}
            <Box
                sx={(theme) => ({
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    ...(!props.displayAsFloatingFooter && {
                        marginTop: theme.spacing(2),
                    }),
                    ...(props.displayAsFloatingFooter && {
                        position: "fixed",
                        bottom: theme.spacing(2),
                    }),
                })}
            >
                <Card
                    onMouseEnter={() => {
                        if (mouseLeaveTimeOut.current)
                            clearTimeout(mouseLeaveTimeOut.current);
                        setShowFullSize(true);
                    }}
                    onMouseLeave={() => {
                        if (mouseLeaveTimeOut.current)
                            clearTimeout(mouseLeaveTimeOut.current);

                        mouseLeaveTimeOut.current = setTimeout(() => {
                            setShowFullSize(false);
                        }, 5000);
                    }}
                    ref={cardRef}
                    elevation={5}
                    sx={(theme) => ({
                        backgroundColor: "black",
                        ...(props.displayAsFloatingFooter && {
                            marginLeft: cardLeftMargin(theme),
                            width: cardWidth(theme),
                            height: showFullSize ? "unset" : "5px",
                        }),
                        transition: theme.transitions.create(
                            ["height", "width", "marginLeft"],
                            {
                                easing: theme.transitions.easing.easeInOut,
                                duration: theme.transitions.duration.shorter,
                            },
                        ),
                    })}
                >
                    <CardActions
                        disableSpacing={true}
                        sx={{
                            justifyContent: "center",
                            marginLeft: "0px",
                        }}
                    >
                        <IconButtonWhite
                            onClick={() =>
                                onAddToOrSubtractFromPlaybackDateTime(
                                    "subtract",
                                    30,
                                )
                            }
                            aria-label="Rewind 30 seconds"
                        >
                            <FastRewind />
                        </IconButtonWhite>

                        {!userRequestedPlayModePlay && (
                            <IconButtonWhite
                                onClick={() =>
                                    dispatch(caseViewerActions.startPlayback())
                                }
                                aria-label="Play"
                            >
                                <PlayArrow />
                            </IconButtonWhite>
                        )}

                        {userRequestedPlayModePlay && (
                            <IconButtonWhite
                                onClick={() =>
                                    dispatch(caseViewerActions.pausePlayback())
                                }
                                aria-label="Pause"
                            >
                                <Pause />
                            </IconButtonWhite>
                        )}

                        <PlaybackSettingsPopOver
                            onPlaybackSpeedChanged={(speed) => {
                                dispatch(
                                    caseViewerActions.setPlaybackSpeed({
                                        speed,
                                    }),
                                );
                                restartTicker();
                            }}
                        />

                        <IconButtonWhite
                            onClick={() =>
                                onAddToOrSubtractFromPlaybackDateTime("add", 30)
                            }
                            aria-label="Skip 30 seconds"
                        >
                            <FastForward />
                        </IconButtonWhite>

                        <FeatureFlagGuard
                            accessibleWithFeatureFlags={[
                                AppFeature.CaseManagementExports,
                            ]}
                        >
                            <DossierExportButton />
                        </FeatureFlagGuard>

                        {showFullSize && <CurrentTime />}
                    </CardActions>
                    <CardContent
                        sx={{
                            paddingTop: 0,
                            visibility: showFullSize ? "hidden" : "visible",
                        }}
                    >
                        <PlaybackTimeline
                            ref={playbackTimeLineRef}
                            startDateTime={props.startDateTime}
                            endDateTime={props.endDateTime}
                            onSetPlaybackDateTime={onSetPlaybackDateTime}
                            onTimelineGroupsChanged={() => {
                                if (cardRef.current) {
                                    setPlaybackControlsHeight(
                                        cardRef.current.getBoundingClientRect()
                                            .height,
                                    );
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </Box>
        </>
    );
}
